import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kmgestao/ui';

import {
  createLeadInputSchema,
  formatCnpj,
  formatPhoneBR,
  normalizeLeadInput,
  useUpdateLead,
  type CreateLeadInput,
  type Lead,
  type LeadAddress,
} from '../../../features/leads/index.js';

import { LeadFormFields } from './LeadFormFields.js';

export interface EditLeadDialogProps {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

/**
 * Dialog de editar lead — reusa `LeadFormFields` com defaults vindos do
 * lead carregado. `respectExistingValues` é `true` para que o autofill
 * do CNPJ (se o user redigitar) **não** sobrescreva dados já editados.
 *
 * Especialidade/origem preservadas do valor atual. Campos vazios
 * continuam recebendo autofill do CNPJ quando o user redigita.
 */
export function EditLeadDialog({ lead, open, onOpenChange, onSaved }: EditLeadDialogProps) {
  const updateLead = useUpdateLead();

  const form = useForm<CreateLeadInput>({
    resolver: zodResolver(createLeadInputSchema),
    defaultValues: toFormDefaults(lead),
  });

  const { handleSubmit, reset, formState } = form;

  // Re-sincroniza o form quando o lead muda externamente (outro admin
  // editou no mesmo momento, React Query revalidou). Sem isso, Karla
  // abre o dialog e vê os dados velhos.
  useEffect(() => {
    if (open) reset(toFormDefaults(lead));
  }, [lead, open, reset]);

  const onSubmit = handleSubmit(async (raw) => {
    // Rede de segurança final — mesmo que o user tenha editado e saído
    // direto via Enter (sem disparar onBlur), os dados chegam ao banco
    // no padrão único do sistema. Ver `formatters.ts::normalizeLeadInput`.
    const values = normalizeLeadInput(raw);
    await updateLead.mutateAsync(
      { id: lead.id, ...values },
      {
        onSuccess: () => {
          toast.success('Lead atualizado.');
          onOpenChange(false);
          onSaved?.();
        },
        onError: (err) =>
          toast.error('Falha ao atualizar', { description: (err as Error).message }),
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar lead</DialogTitle>
          <DialogDescription>
            Atualize qualquer campo. CNPJ pode disparar novo autofill se preencher vazios — dados já
            salvos são preservados.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate>
          <LeadFormFields form={form} idPrefix="edit-lead" respectExistingValues />

          <DialogFooter className="mt-6 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" size="lg">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" size="lg" disabled={formState.isSubmitting}>
              {formState.isSubmitting ? 'Salvando…' : 'Salvar alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Converte um `Lead` (snapshot do banco) para os defaults do form
 * (`CreateLeadInput`). Mascara CNPJ/telefone para corresponder ao que
 * o user verá digitado. */
function toFormDefaults(lead: Lead): CreateLeadInput {
  const specialty = lead.specialty as CreateLeadInput['specialty'] | null;
  const source = (lead.source ?? 'manual') as CreateLeadInput['source'];
  return {
    full_name: lead.full_name,
    legal_name: lead.legal_name,
    clinic_name_guess: lead.clinic_name_guess,
    email: lead.email,
    phone: lead.phone ? formatPhoneBR(lead.phone) : null,
    cnpj: lead.cnpj ? formatCnpj(lead.cnpj) : null,
    address: (lead.address as LeadAddress) ?? {},
    specialty,
    source,
    notes: lead.notes,
  };
}
